import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  Collapse,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Flag as EpicIcon,
  Book as StoryIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatEmployeeName = (employee) => {
  if (!employee) return '';
  const first = employee.first_name || employee.firstName || '';
  const last = employee.last_name || employee.lastName || '';
  return `${first} ${last}`.trim() || employee.name || employee.username || 'Unnamed Employee';
};

const EnhancedEpicSelectionDialog = ({ 
  open, 
  onClose, 
  projectId, 
  projectName,
  companyId,
  onEpicsConfirmed,
  teamMembers = [],
  leadId = null,
  productOwnerId = null
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [suggestedEpics, setSuggestedEpics] = useState([]);
  const [themeInfo, setThemeInfo] = useState(null);
  const [selectedEpics, setSelectedEpics] = useState({});
  const [selectedStories, setSelectedStories] = useState({});
  const [epicAssignments, setEpicAssignments] = useState({});
  const [storyAssignments, setStoryAssignments] = useState({});
  const [creatingEpics, setCreatingEpics] = useState(false);
  const [creationProgress, setCreationProgress] = useState('');
  
  // Track editing states
  const [editingEpics, setEditingEpics] = useState({});
  const [editingStories, setEditingStories] = useState({});

  useEffect(() => {
    if (open && projectId && companyId) {
      fetchEpicSuggestions();
    }
  }, [open, projectId, companyId]);

  const fetchEpicSuggestions = async () => {
    if (!projectId || !companyId) {
      setError('Missing project or company information');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/issues/generate-epics?project_id=${projectId}&company_id=${companyId}`,
        {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (result.code === "AI_PLAN_UPGRADE_REQUIRED") {
          const msg = result.message || "AI features are not included in your current plan. Please upgrade to unlock AI.";
          setError(msg);
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          return;
        } else if (result.code === "AI_TOKENS_EXHAUSTED") {
          const msg = `⚠️ ${result.message || "You have used all your AI tokens for this billing cycle."}`;
          setError(msg);
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          return;
        } else if (result.code === "AI_FEATURE_LOCKED") {
          const msg = result.message || "Epic generation feature is not enabled for your plan.";
          setError(msg);
          setSnackbar({
            open: true,
            message: msg,
            severity: "warning"
          });
          return;
        }

        throw new Error(result.message || 'Failed to generate epics');
      }

      if (result.success && result.data.epics && result.data.epics.length > 0) {
        setSuggestedEpics(result.data.epics);
        setThemeInfo(result.data.theme || null);
        
        // Initialize selections
        const autoSelectedEpics = {};
        const autoSelectedStories = {};
        const initialEpicAssignments = {};
        const initialStoryAssignments = {};
        const firstMember = teamMembers[0];
        const defaultAssignee = firstMember ? (firstMember.employee_id || firstMember.id || firstMember.employeeId) : (leadId || null);

        result.data.epics.forEach((epic, epicIndex) => {
          autoSelectedEpics[epicIndex] = true;
          
          initialEpicAssignments[epicIndex] = {
            assignee: null,
            lead: null,
            productOwner: null,
          };

          if (epic.stories) {
            epic.stories.forEach((story, storyIndex) => {
              autoSelectedStories[`${epicIndex}_${storyIndex}`] = true;
              
              const hasDev = teamMembers.some(m => (m.employee_id || m.id || m.employeeId) === story.suggestedDeveloperId);

              initialStoryAssignments[`${epicIndex}_${storyIndex}`] = {
                assignee: hasDev ? story.suggestedDeveloperId : '',
              };
            });
          }
        });

        setSelectedEpics(autoSelectedEpics);
        setSelectedStories(autoSelectedStories);
        setEpicAssignments(initialEpicAssignments);
        setStoryAssignments(initialStoryAssignments);
        
        setSnackbar({
          open: true,
          message: `✨ Successfully generated ${result.data.epics.length} epics from PRD!`,
          severity: "success"
        });
      } else {
        throw new Error(result.message || 'No epics generated');
      }
    } catch (err) {
      //console.error('Error fetching epics:', err);
      setError(err.message);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to generate epics',
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEpic = (index) => {
    setSelectedEpics(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleToggleStory = (epicIndex, storyIndex) => {
    const key = `${epicIndex}_${storyIndex}`;
    setSelectedStories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleEpicAssignmentChange = (epicIndex, field, value) => {
    setEpicAssignments(prev => ({
      ...prev,
      [epicIndex]: {
        ...prev[epicIndex],
        [field]: value
      }
    }));
  };

  const handleStoryAssignmentChange = (epicIndex, storyIndex, field, value) => {
    const key = `${epicIndex}_${storyIndex}`;
    setStoryAssignments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // Inline edit state handlers
  const handleEditEpicToggle = (epicIndex, e) => {
    if (e) e.stopPropagation();
    setEditingEpics(prev => ({
      ...prev,
      [epicIndex]: !prev[epicIndex]
    }));
  };

  const handleEditStoryToggle = (epicIndex, storyIndex) => {
    const key = `${epicIndex}_${storyIndex}`;
    setEditingStories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleEditEpicChange = (epicIndex, field, value) => {
    setSuggestedEpics(prev => {
      const updated = [...prev];
      updated[epicIndex] = {
        ...updated[epicIndex],
        [field]: value
      };
      return updated;
    });
  };

  const handleEditStoryChange = (epicIndex, storyIndex, field, value) => {
    setSuggestedEpics(prev => {
      const updated = [...prev];
      const epic = { ...updated[epicIndex] };
      const stories = [...epic.stories];
      stories[storyIndex] = {
        ...stories[storyIndex],
        [field]: value
      };
      epic.stories = stories;
      updated[epicIndex] = epic;
      return updated;
    });
  };

  const handleConfirmAndCreate = async () => {
    const epicsToCreate = suggestedEpics.filter((_, index) => selectedEpics[index]);

    if (epicsToCreate.length === 0) {
      setError('Please select at least one epic to create');
      return;
    }

    setCreatingEpics(true);
    setError(null);
    setCreationProgress('Starting epic and story creation...');

    try {
      const createdEpics = [];
      let totalStoriesCreated = 0;
      const token = localStorage.getItem('token');

      // Create selected Epics and their nested stories
      for (let i = 0; i < suggestedEpics.length; i++) {
        if (!selectedEpics[i]) continue;

        const epic = suggestedEpics[i];
        setCreationProgress(`Creating Epic ${i + 1}/${suggestedEpics.length}: ${epic.title}...`);

        // Assemble Epic Description block cleanly without markdown, skipping empty fields
        const descriptionParts = [];
        if (epic.epicStatement && epic.epicStatement.trim()) {
          descriptionParts.push(`Epic Statement:\n${epic.epicStatement.trim()}`);
        }
        if (epic.moduleOverview && epic.moduleOverview.trim()) {
          descriptionParts.push(`Overview:\n${epic.moduleOverview.trim()}`);
        }
        if (epic.businessObjective && epic.businessObjective.trim()) {
          descriptionParts.push(`Business Objective:\n${epic.businessObjective.trim()}`);
        }
        if (epic.detailedDescription && epic.detailedDescription.trim()) {
          descriptionParts.push(`Detailed Description:\n${epic.detailedDescription.trim()}`);
        }
        if (Array.isArray(epic.features) && epic.features.filter(Boolean).map(f => f.trim()).filter(f => f.length > 0).length > 0) {
          descriptionParts.push(`Features:\n${epic.features.filter(Boolean).map(f => f.trim()).filter(f => f.length > 0).join('\n')}`);
        }
        if (Array.isArray(epic.functionalRequirements) && epic.functionalRequirements.filter(Boolean).map(f => f.trim()).filter(f => f.length > 0).length > 0) {
          descriptionParts.push(`Functional Requirements:\n${epic.functionalRequirements.filter(Boolean).map(f => f.trim()).filter(f => f.length > 0).join('\n')}`);
        }
        
        const acList = Array.isArray(epic.acceptanceCriteria) 
          ? epic.acceptanceCriteria.filter(Boolean).map(a => a.trim()).filter(a => a.length > 0)
          : [epic.successCriteria].filter(Boolean).map(s => s.trim()).filter(s => s.length > 0);
        
        if (acList.length > 0) {
          descriptionParts.push(`Acceptance Criteria:\n${acList.join('\n')}`);
        }

        const epicDescription = descriptionParts.join('\n\n');

        const epicData = {
          project_id: projectId,
          issueType: 2, // Epic
          summary: epic.title,
          description: epicDescription,
          priority: 3,
          status: 1,
          company_id: companyId,
          assignee: null,
          lead: null,
          productOwner: null,
          startDate: epic.startDate,
          endDate: epic.endDate,
          timeEstimate: epic.timeEstimate || 160,
          reporter: null,
          watcher: null,
          labels: [],
          severity: 3,
        };

        const response = await fetch(
          `${API_URL}/api/issues`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(epicData)
          }
        );

        const result = await response.json();

        if (response.ok && result.success && result.data) {
          const createdEpicId = result.data.id;
          createdEpics.push(result.data);

          // Create nested stories for this epic
          if (epic.stories && epic.stories.length > 0) {
            for (let j = 0; j < epic.stories.length; j++) {
              const storyKey = `${i}_${j}`;
              if (!selectedStories[storyKey]) continue;

              const story = epic.stories[j];
              setCreationProgress(`Creating Story for ${epic.title}: ${story.title}...`);

              const sAssignments = storyAssignments[storyKey] || {};
              
              // Assemble rich markdown description for the User Story
              const storyDescriptionParts = [];
              if (story.story) {
                storyDescriptionParts.push(`**Story:**\n${story.story}`);
              }
              if (story.clearDescription) {
                storyDescriptionParts.push(`**Description:**\n${story.clearDescription}`);
              } else if (story.description) {
                storyDescriptionParts.push(`**Description:**\n${story.description}`);
              }
              if (Array.isArray(story.inputs) && story.inputs.length > 0) {
                storyDescriptionParts.push(`**Inputs:**\n${story.inputs.map(inp => `- ${inp}`).join('\n')}`);
              }
              if (Array.isArray(story.outputs) && story.outputs.length > 0) {
                storyDescriptionParts.push(`**Outputs:**\n${story.outputs.map(out => `- ${out}`).join('\n')}`);
              }
              if (Array.isArray(story.acceptanceCriteria) && story.acceptanceCriteria.length > 0) {
                storyDescriptionParts.push(`**Acceptance Criteria:**\n${story.acceptanceCriteria.map(a => `- ${a}`).join('\n')}`);
              }
              if (Array.isArray(story.definitionOfDone) && story.definitionOfDone.length > 0) {
                storyDescriptionParts.push(`**Definition of Done:**\n${story.definitionOfDone.map(d => `- ${d}`).join('\n')}`);
              }
              const storyDescription = storyDescriptionParts.join('\n\n');

              const storyData = {
                project_id: projectId,
                issueType: 3, // Story
                summary: story.title,
                description: storyDescription,
                priority: 3,
                status: 1,
                company_id: companyId,
                epic_id: createdEpicId,
                assignee: null,
                lead: null,
                productOwner: null,
                reviewer: null,
                startDate: story.startDate,
                endDate: story.endDate,
                timeEstimate: story.timeEstimate || 40,
                reporter: null,
                watcher: null,
                labels: [],
                severity: 3,
                sprintName: story.sprint || 'Sprint 1',
                storyPoints: story.storyPoints || 5
              };

              const storyResponse = await fetch(
                `${API_URL}/api/issues`,
                {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify(storyData)
                }
              );

              const storyResult = await storyResponse.json();
              if (storyResponse.ok && storyResult.success) {
                totalStoriesCreated++;
              } else {
                //console.error(`   ❌ Failed to create story: ${story.title}`, storyResult.message);
              }
            }
          }
        } else {
          //console.error(`❌ Failed to create epic: ${epic.title}`, result.message);
        }
      }

      if (createdEpics.length > 0) {
        if (onEpicsConfirmed) {
          onEpicsConfirmed(createdEpics);
        }
        onClose();
      } else {
        setError('No epics were created. Please check logs.');
      }
    } catch (err) {
      //console.error('Error creating hierarchy:', err);
      setError('Failed to save the epics and stories. Please try again.');
    } finally {
      setCreatingEpics(false);
      setCreationProgress('');
    }
  };

  const selectedCount = Object.values(selectedEpics).filter(Boolean).length;
  const selectedStoriesCount = Object.values(selectedStories).filter(Boolean).length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={creatingEpics}
    >
      <DialogTitle sx={{ bgcolor: 'primary.dark', color: '#white', py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <AutoAwesomeIcon sx={{ color: 'amber.400' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
              AI Hierarchy Builder: Epics & Stories
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Chip 
              icon={<EpicIcon sx={{ color: '#fff !important' }} />}
              label={`${selectedCount} Epics`}
              color="secondary"
              variant="filled"
              sx={{ fontWeight: 'bold', bgcolor: '#9c27b0' }}
            />
            <Chip 
              icon={<StoryIcon sx={{ color: '#fff !important' }} />}
              label={`${selectedStoriesCount} Stories`}
              color="info"
              variant="filled"
              sx={{ fontWeight: 'bold', bgcolor: '#2196f3' }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#f4f6f8', px: { xs: 2, md: 4 }, py: 3 }}>
        {creationProgress && (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <CircularProgress size={60} thickness={4} />
            <Typography sx={{ mt: 3, fontWeight: 'medium' }} color="primary.main" variant="h6">
              {creationProgress}
            </Typography>
          </Box>
        )}

        {loading && !creationProgress && (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <CircularProgress size={60} thickness={4} />
            <Typography sx={{ mt: 3 }} color="text.secondary" variant="body1">
              Analyzing PRD document and generating epics and stories...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !creationProgress && suggestedEpics.length > 0 && (
          <>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                🎉 AI successfully analyzed your PRD! We found <strong>{suggestedEpics.length} main modules (Epics)</strong>. 
                Expand each Epic to view/edit its nested <strong>User Stories (Features)</strong> and assign team members directly before saving.
              </Typography>
            </Alert>

            {themeInfo && (
              <Card variant="outlined" sx={{ mb: 3.5, p: 3, borderRadius: 3, border: '1px solid #e0e0e0', background: 'linear-gradient(135deg, #fbfcfd 0%, #f4f8fb 100%)', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <Typography variant="caption" fontWeight="bold" color="secondary.main" sx={{ textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                  🎨 Project Theme
                </Typography>
                <Typography variant="h5" fontWeight="800" color="primary.dark" gutterBottom>
                  {themeInfo.themeName}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ fontStyle: 'italic', pl: 1, borderLeft: '3px solid #9c27b0' }}>
                  {themeInfo.themeDescription}
                </Typography>
                {themeInfo.businessGoals && themeInfo.businessGoals.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                      🎯 Business Goals
                    </Typography>
                    <Grid container spacing={1}>
                      {themeInfo.businessGoals.map((goal, gIdx) => (
                        <Grid item xs={12} sm={6} key={gIdx}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                            <Typography variant="body2" color="text.primary">
                              {goal}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Card>
            )}

            <Box>
              {suggestedEpics.map((epic, epicIndex) => {
                const isEpicSelected = selectedEpics[epicIndex];
                const isEpicEditing = editingEpics[epicIndex];
                const epicAssigneeVal = epicAssignments[epicIndex]?.assignee || '';
                const epicLeadVal = epicAssignments[epicIndex]?.lead || '';
                const epicPOVal = epicAssignments[epicIndex]?.productOwner || '';
                const storiesForEpic = epic.stories || [];
                const selectedStoriesForThisEpic = storiesForEpic.filter((_, sIdx) => selectedStories[`${epicIndex}_${sIdx}`]).length;

                return (
                  <Accordion 
                    key={epicIndex}
                    expanded={isEpicSelected}
                    sx={{ 
                      mb: 2.5, 
                      borderRadius: '8px !important', 
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      border: '1px solid',
                      borderColor: isEpicSelected ? '#9c27b0' : 'divider',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={isEpicSelected ? <ExpandMoreIcon /> : null}
                      onClick={() => handleToggleEpic(epicIndex)}
                      sx={{ 
                        bgcolor: isEpicSelected ? 'rgba(156, 39, 176, 0.04)' : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                        py: 1
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%" gap={2}>
                        <Checkbox 
                          checked={isEpicSelected || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleEpic(epicIndex);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <EpicIcon sx={{ color: '#9c27b0' }} />
                        <Box flexGrow={1}>
                          {isEpicEditing ? (
                            <TextField
                              size="small"
                              value={epic.title}
                              onChange={(e) => handleEditEpicChange(epicIndex, 'title', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              fullWidth
                              placeholder="Epic Title"
                              sx={{ bgcolor: '#fff', borderRadius: 1 }}
                            />
                          ) : (
                            <Typography variant="h6" fontWeight="bold" color={isEpicSelected ? '#9c27b0' : 'text.primary'}>
                              {epic.title}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            📅 Period: {epic.startDate} to {epic.endDate} | ⏱️ Duration: {epic.durationDays} days ({epic.timeEstimate} hrs)
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleEditEpicToggle(epicIndex, e)}
                          sx={{ color: '#9c27b0' }}
                        >
                          {isEpicEditing ? <SaveIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                        </IconButton>
                        {isEpicSelected && (
                          <Chip 
                            label={`${selectedStoriesForThisEpic}/${storiesForEpic.length} Stories`}
                            color="info"
                            size="small"
                            sx={{ fontWeight: 'bold', bgcolor: '#2196f3', color: '#fff' }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ bgcolor: 'background.paper', px: 3, pb: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          {isEpicEditing ? (
                            <Box mb={2}>
                              <Typography variant="subtitle2" color="#9c27b0" fontWeight="bold">
                                Epic Statement
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={epic.epicStatement || ''}
                                onChange={(e) => handleEditEpicChange(epicIndex, 'epicStatement', e.target.value)}
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          ) : (
                            epic.epicStatement && (
                              <Box mb={2} p={1.5} sx={{ bgcolor: '#fbf0f9', borderRadius: 1.5, borderLeft: '3px solid #9c27b0' }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                  Epic Statement
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" color="text.primary">
                                  {epic.epicStatement}
                                </Typography>
                              </Box>
                            )
                          )}

                          <Box mb={2}>
                            <Typography variant="subtitle2" color="#9c27b0" fontWeight="bold" display="flex" alignItems="center" gap={0.5}>
                              <EpicIcon fontSize="small" /> Module Overview
                            </Typography>
                            {isEpicEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={epic.moduleOverview || ''}
                                onChange={(e) => handleEditEpicChange(epicIndex, 'moduleOverview', e.target.value)}
                                sx={{ mt: 1 }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                                {epic.moduleOverview}
                              </Typography>
                            )}
                          </Box>

                          <Box mb={2}>
                            <Typography variant="subtitle2" color="#9c27b0" fontWeight="bold" display="flex" alignItems="center" gap={0.5}>
                              <AssignmentIcon fontSize="small" /> Detailed Description
                            </Typography>
                            {isEpicEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={epic.detailedDescription || ''}
                                onChange={(e) => handleEditEpicChange(epicIndex, 'detailedDescription', e.target.value)}
                                sx={{ mt: 1 }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                                {epic.detailedDescription}
                              </Typography>
                            )}
                          </Box>

                          {epic.businessObjective && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" color="#9c27b0" fontWeight="bold">
                                Business Objective
                              </Typography>
                              {isEpicEditing ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  value={epic.businessObjective || ''}
                                  onChange={(e) => handleEditEpicChange(epicIndex, 'businessObjective', e.target.value)}
                                  sx={{ mt: 1 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                                  {epic.businessObjective}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Grid>

                        {/* Stories Nested List */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" fontWeight="bold" color="#2196f3" gutterBottom display="flex" alignItems="center" gap={1}>
                            <StoryIcon /> User Stories under this Epic ({storiesForEpic.length})
                          </Typography>

                          {storiesForEpic.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No user stories generated for this epic.
                            </Typography>
                          ) : (
                            <Box sx={{ mt: 1.5 }}>
                              {storiesForEpic.map((story, storyIndex) => {
                                const storyKey = `${epicIndex}_${storyIndex}`;
                                const isStorySelected = selectedStories[storyKey];
                                const isStoryEditing = editingStories[storyKey];
                                const storyAssigneeVal = storyAssignments[storyKey]?.assignee || '';
                                const storyLeadVal = storyAssignments[storyKey]?.lead || '';
                                const storyPOVal = storyAssignments[storyKey]?.productOwner || '';
                                const storyReviewerVal = storyAssignments[storyKey]?.reviewer || '';

                                return (
                                  <Card 
                                    key={storyIndex} 
                                    variant="outlined" 
                                    sx={{ 
                                      mb: 2, 
                                      borderRadius: 2,
                                      borderColor: isStorySelected ? '#2196f3' : 'divider',
                                      bgcolor: isStorySelected ? '#fafcff' : 'background.paper',
                                      boxShadow: isStorySelected ? '0 2px 8px rgba(0,0,0,0.02)' : 'none'
                                    }}
                                  >
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                      <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                        <Checkbox 
                                          size="small"
                                          checked={isStorySelected || false}
                                          onChange={() => handleToggleStory(epicIndex, storyIndex)}
                                        />
                                        <StoryIcon sx={{ color: '#2196f3' }} />
                                        <Box flexGrow={1}>
                                          {isStoryEditing ? (
                                            <TextField
                                              size="small"
                                              value={story.title}
                                              onChange={(e) => handleEditStoryChange(epicIndex, storyIndex, 'title', e.target.value)}
                                              fullWidth
                                              placeholder="Story Title"
                                              sx={{ bgcolor: '#fff', borderRadius: 1 }}
                                            />
                                          ) : (
                                            <Typography variant="subtitle1" fontWeight="bold" color={isStorySelected ? '#2196f3' : 'text.primary'}>
                                              {story.title}
                                            </Typography>
                                          )}
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            🕒 Duration: {story.durationDays} days ({story.timeEstimate} hrs) | Timeline: {story.startDate} to {story.endDate}
                                          </Typography>
                                        </Box>
                                        <IconButton 
                                          size="small" 
                                          onClick={() => handleEditStoryToggle(epicIndex, storyIndex)}
                                          sx={{ color: '#2196f3' }}
                                        >
                                          {isStoryEditing ? <SaveIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                        </IconButton>
                                      </Box>

                                      <Collapse in={isStorySelected}>
                                        <Box pl={5.5}>
                                          {isStoryEditing ? (
                                            <TextField
                                              fullWidth
                                              multiline
                                              rows={3}
                                              value={story.description || ''}
                                              onChange={(e) => handleEditStoryChange(epicIndex, storyIndex, 'description', e.target.value)}
                                              sx={{ mb: 2 }}
                                            />
                                          ) : (
                                            <>
                                              {story.story && (
                                                <Box mb={1.5} p={1.5} sx={{ bgcolor: '#f4f6f8', borderRadius: 1.5, borderLeft: '3px solid #2196f3' }}>
                                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                    Story Goal
                                                  </Typography>
                                                  <Typography variant="body2" fontWeight="medium" color="text.primary">
                                                    {story.story}
                                                  </Typography>
                                                </Box>
                                              )}

                                              {story.clearDescription && (
                                                <Box mb={1.5}>
                                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                    Functional Description
                                                  </Typography>
                                                  <Typography variant="body2" color="text.primary">
                                                    {story.clearDescription}
                                                  </Typography>
                                                </Box>
                                              )}
                                              
                                              {!story.story && !story.clearDescription && story.description && (
                                                <Box mb={1.5}>
                                                  <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                    Description
                                                  </Typography>
                                                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
                                                    {story.description}
                                                  </Typography>
                                                </Box>
                                              )}
                                            </>
                                          )}

                                          {story.inputs && story.inputs.length > 0 && (
                                            <Box mb={1.5}>
                                              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                Expected Inputs
                                              </Typography>
                                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {story.inputs.map((inp, inpIdx) => (
                                                  <Chip key={inpIdx} label={inp} size="small" variant="outlined" />
                                                ))}
                                              </Box>
                                            </Box>
                                          )}

                                          {story.outputs && story.outputs.length > 0 && (
                                            <Box mb={1.5}>
                                              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                Expected Outputs
                                              </Typography>
                                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {story.outputs.map((out, outIdx) => (
                                                  <Chip key={outIdx} label={out} size="small" variant="outlined" color="success" />
                                                ))}
                                              </Box>
                                            </Box>
                                          )}

                                          {story.acceptanceCriteria && story.acceptanceCriteria.length > 0 && (
                                            <Box mb={1.5}>
                                              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                Acceptance Criteria
                                              </Typography>
                                              <List dense disablePadding>
                                                {story.acceptanceCriteria.map((ac, acIdx) => (
                                                  <ListItem key={acIdx} disableGutters sx={{ py: 0.25 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                      • {ac}
                                                    </Typography>
                                                  </ListItem>
                                                ))}
                                              </List>
                                            </Box>
                                          )}

                                          {story.definitionOfDone && story.definitionOfDone.length > 0 && (
                                            <Box mb={2}>
                                              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                                Definition of Done (DoD)
                                              </Typography>
                                              <List dense disablePadding>
                                                {story.definitionOfDone.map((dod, dodIdx) => (
                                                  <ListItem key={dodIdx} disableGutters sx={{ py: 0.25 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                      ✓ {dod}
                                                    </Typography>
                                                  </ListItem>
                                                ))}
                                              </List>
                                            </Box>
                                          )}

                                         </Box>
                                      </Collapse>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#f4f6f8' }}>
        <Button 
          onClick={onClose} 
          disabled={creatingEpics}
          variant="outlined"
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmAndCreate}
          variant="contained"
          color="primary"
          disabled={loading || creatingEpics || selectedCount === 0}
          startIcon={creatingEpics ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
          sx={{ px: 4, fontWeight: 'bold' }}
        >
          {creatingEpics 
            ? 'Saving Requirements...' 
            : `Create Selected Hierarchy (${selectedCount} Epics + ${selectedStoriesCount} Stories)`
          }
        </Button>
      </DialogActions>

      {/* Top Center Alert for AI Feedback & Limits */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EnhancedEpicSelectionDialog;